import {createSlice} from '@reduxjs/toolkit'
import * as CONST from '../../app/constants'
import { fullName } from '../../app/utils'
import { bltCollectionAgencyReviewData } from '../selector/BLTReviewData/collectionsReviewData'
import { changeMainAgentLicenceSubTypeForApproval, changeRequestRelatedLicenceSubType, newMainAgentChangeRequestWithData, renewalRelatedLicenceSubType, revertChangeRequestRelatedLicenceSubType } from './applicationsUtils'
import {setCompanyRjscDetails} from './rjscDetailReviewSlice'

export const INTERNAL_ID_PREFIX = "INTERNAL"

export function InternalId(licencingSubType) {
    let internalId = window.sessionStorage.getItem('internalId')
    let retId = ""

    if(!internalId)
        internalId = 1
    else 
        internalId++

    switch(licencingSubType) { 
        case CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION:
        case CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_APPLICATION :
        case CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_NEW_APPLICATION :
        case CONST.LICENCE_SUB_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_APPLICATION :
        case CONST.LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_APPLICATION :
            retId = `${INTERNAL_ID_PREFIX}-agency-${internalId}`
            break
        case CONST.LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_APPLICATION : 
        case CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_APPLICATION :
        case CONST.LICENCE_SUB_TYPE_PRINCIPAL_BROKER_NEW_APPLICATION :
            retId = `${INTERNAL_ID_PREFIX}-agent-${internalId}`
            break
        default :
            retId = `${INTERNAL_ID_PREFIX}--${internalId}`
            break
    }

    window.sessionStorage.setItem('internalId', internalId)

    return retId
}

export function IsInternalId(applicationId) { 
    if(applicationId && applicationId.toString().indexOf(INTERNAL_ID_PREFIX) !== -1) { 
        return true
    }

    return false
}

export const LICENCED_INDIVIDUAL = "LICENCED_INDIVIDUAL";
export const LICENCED_UNDER_BROKERAGE = "LICENCED_UNDER_BROKERAGE";
export const LICENCED_EXEMPT = "LICENCED_EXEMPT";
export const NOT_LICENCED = "NOT_LICENCED";

// Put MRA data here as well
export const BusinessLicenceDataInitObject = () => ({
    "personData": null,
    "businessName": null,
    "companyTrademark": null,
    "registryNumber": null,
    "licenceAnotherProvince": null,
    "licenceHistoryData": null,
    "serviceBusinessAddress": null,
    "mainBusinessContact": {},
    "complianceOfficer": null,
    "responsibleAgent" : null,
    "serviceCivicAddress": null,
    "financialRecordsLocation": {},
    "licenceHistoryList": [],
    "licenceSuspendedData": [],
    "additionalBranches": [],
    "trustAccountInstitution": null,
    "confirmSuretyBond": 0,
    "suspendedElsewhere": null,
    "updatedInRjsc": 0,
    "startDate": null,
    "workHistoryData": [],
    "criminalHistoryData": [],
    "businessLicenceElsewhere": false,
    "reviewDataCollectionDebt": null,
    "internalNotes": "",
    "companyRjscDetails": null,
    "internalNoteList": [],
    "submittedDate": null,

    // TBD : Delete after app is stable
    //hasCompletedRegInfoProgram: false,
    //isPrincipalBroker: false,
    //hasRoleWithRJSC: false,
    //hasWorkExperience: false,
    //isResidentOfCanada: false,
    //isOldEnough: false,
    previouslyLicencedStatus: null,
    mblraLicenceNumber: null,
    companyFiscalEndDate: 0,
    principalBrokerDutiesConfirmation: false,
    userApplicationReviewComplete : false,
    licenceUpgradeApplicationId: 0,
    brokerLicenceNumber: null,
    currentlyBrokerInNS: false
})

export const loadMRAState = () => {
    try {
      const serializedState = localStorage.getItem("mra-state");
      if (serializedState === null) {
        return BusinessLicenceDataInitObject;
      }
      return JSON.parse(serializedState);
    } catch (err) {
      return BusinessLicenceDataInitObject;
    }
  };

export const BusinessApplicationInitObject = (licencingSubType = CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION) => ({
    "id": InternalId(licencingSubType),
    "licencingSubType": licencingSubType,
    "applicationStatus": "PENDING_PAYMENT",
    "licenceStatus": "NEW",
    "name": null,
    "submittedDate": 0,
    "markedDeleted": false,
    "markedDoNotRenew": false,
    "licenceNumber": "",
    "endDate": null,
    "businessApplicationId": null,
    "locked": null,
    "changeType" : "SAVE",
    "businessApplicationHistoryData" : [],
    "businessLicenceData": BusinessLicenceDataInitObject(),
    "documents": []
})

const primaryAgencyApplicationSlice = createSlice({
    name : 'primaryAgencyApplication',
    initialState : {
        application: null,
        mortgageCacheProperties:{},
        serverRequestStatus : CONST.REQUEST_STATUS.IDLE,
        ...loadMRAState()
    },
    reducers : {
        createNewBusinessApplicationForApplicationType(state,action) {
            let licencingSubType = CONST.LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION 

            if(action.payload === CONST.APPLICATION_TYPE_DEBT_MANAGEMENT_AGENCY){
                licencingSubType = CONST.LICENCE_SUB_TYPE_DEBT_MANAGEMENT_APPLICATION
            } else if(action.payload === CONST.APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE) { 
                licencingSubType = CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_NEW_APPLICATION
            } else if(action.payload === CONST.APPLICATION_TYPE_MORTGAGE_LENDER_LICENCE) {
                licencingSubType = CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_NEW_APPLICATION
            } else if(action.payload === CONST.APPLICATION_TYPE_MORTGAGE_ADMINISTRATOR_LICENCE){
                licencingSubType = CONST.LICENCE_SUB_TYPE_MORTGAGE_ADMINISTRATOR_NEW_APPLICATION
            } else if(action.payload === CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_LICENCE) {
                licencingSubType = CONST.LICENCE_SUB_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_APPLICATION
            } else if(action.payload === CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_LICENCE) {
                licencingSubType = CONST.LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_APPLICATION
            } else if(action.payload === CONST.APPLICATION_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_RETENTION_LICENCE) {
                licencingSubType = CONST.LICENCE_SUB_TYPE_INDIVIDUAL_PRINCIPAL_BROKER_RETENTION_APPLICATION
            } else if(action.payload === CONST.APPLICATION_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_LICENCE) {
                licencingSubType = CONST.LICENCE_SUB_TYPE_INDIVIDUAL_COMPLIANCE_OFFICER_RETENTION_APPLICATION
            }

            state.application = BusinessApplicationInitObject(licencingSubType)
        },
        resetPrimaryApplicationData(state, action) {
            let application = BusinessApplicationInitObject(state.application.licencingSubType)
            application.id = state.application.id
            state.application = application
        },
        markPrimaryApplicationDelete(state, action) {
            state.application.markedDeleted = true;
        },
        setAgencyDataForRenewal(state, action) {
            const licenceSubType = renewalRelatedLicenceSubType(state.application.licencingSubType)
            const appData = state.application.businessLicenceData ?? {}

            state.application = {
                ...state.application, 
                licenceStatus : CONST.LICENCE_STATUS_NEW,
                applicationStatus : CONST.APPLICATION_STATUS_PENDING_PAYMENT,
                licencingSubType : licenceSubType,
                businessLicenceData: {
                    ...appData,
                    criminalHistoryData : null,
                    reviewDataCollectionDebt : null,
                    licenceSuspendedData : null,
                    userApplicationReviewComplete : false,
                    licenceUpgradeApplicationId: 0,
                    brokerLicenceNumber: null,
                    currentlyBrokerInNS: false
                }
            }
        },
        setAgencyApplication(state, action) {
            state.application = action.payload
        },
        setAgencyApplicationLicenceSubType(state, action) {
            state.application.licencingSubType = action.payload
        },
        setCompanyTrademark(state, action) {
            state.application.businessLicenceData.companyTrademark = action.payload
        },
        setAgencyBusinessContact(state, action) {
            state.application.businessLicenceData = {
                ...state.application.businessLicenceData,
                ...action.payload
            }

            state.application.contactName = fullName(action.payload.mainBusinessContact)
        },
        setAgencyAddressForService(state, action) {
            state.application.businessLicenceData.serviceCivicAddress = action.payload
        },
        setAgencyAddressForFinancialRecords(state, action) {
            state.application.businessLicenceData.financialRecordsLocation = action.payload
        },
        setAgencyLicenceHistoryDetails (state, action) {
            state.application.businessLicenceData.licenceHistoryList = action.payload
        },
        setAgencyCriminalHistoryDetails (state, action) {
            state.application.businessLicenceData.criminalHistoryData = action.payload
        },
        setAgencyWorkHistoryDetails (state, action) {
            state.application.businessLicenceData.workHistoryData = action.payload
        },
        setAgencyLicenceSuspensionDetails (state, action) {
            state.application.businessLicenceData.licenceSuspendedData = action.payload
        },
        setAgencyDocumentsIsUpdatedReset(state, action) {
            state.application.documents = state.application?.documents?.map((doc)=>{
                if(doc.isUpdated) {
                    doc.isUpdated = false;
                    doc.changeType = "UPDATE";
                }
                return doc
            })
        },
        setAgencySupportingDocuments (state, action) {
            state.application.documents = action.payload
        },
        setAgencyCriminalRecordsDocuments(state, action) {
            const documents = action.payload
            if ((Array.isArray(documents) && documents.length > 0)) {
                const otherDocs = state.application.documents.filter(docs => docs.documentType !== CONST.DOCUMENT_TYPE_CRIMINAL_RECORD)
                state.application.documents = [...otherDocs,...documents]
            }
        },
        setAgencyWorkHistoryDocuments(state, action) {
            const documents = action.payload
            if ((Array.isArray(documents) && documents.length > 0)) {
                const otherDocs = state.application.documents.filter(docs => docs.documentType !== CONST.DOCUMENT_TYPE_WORK_EXPERIENCE)
                state.application.documents = [...otherDocs,...documents]
            }
        },
        setAgencyBrokerCourseCertificateDocuments(state, action) {
            const documents = action.payload
            if ((Array.isArray(documents) && documents.length > 0)) {
                const otherDocs = state.application.documents.filter(docs => docs.documentType !== CONST.DOCUMENT_TYPE_BROKER_COURSE_CERTIFICATE)
                state.application.documents = [...otherDocs,...documents]
            }
        },
        addMortgageBrokerageInsuranceDocuments(state, action) {
            const docObj = {...action.payload} 
            if (Object.keys(docObj).length > 0) {
                state.application.documents = [...state.application.documents.filter(d => d.documentType !== CONST.DOCUMENT_TYPE_INSURANCE_PROOF),docObj]
            }
        },
        addMortgageBrokerageAIRDocuments(state, action) {
            const docObj = {...action.payload}
            if (Object.keys(docObj).length > 0) {
                state.application.documents = [...state.application.documents.filter(d => d.documentType !== CONST.DOCUMENT_TYPE_ANNUAL_INFORMATION_RETURN),docObj]
            }
        },
        addMortgageBrokerageInsuranceContinuationDocuments(state, action) { 
            let docObj = {...action.payload}
            if (Object.keys(docObj).length > 0) {
                if(!docObj.id) {
                    if(docObj.changeType !== "DELETE") {
                        docObj.id = InternalId()
                        state.application.documents = [...state.application.documents, docObj]
                    }
                } else {
                    const docIndex = state.application.documents.findIndex(doc => (doc.id && (doc.id.toString() === docObj.id.toString())))
                    if(docIndex >= 0) {
                        if(IsInternalId(docObj.id) && docObj.changeType === "DELETE") {
                            state.application.documents.splice(docIndex, 1)
                        } else {
                            state.application.documents.splice(docIndex, 1, docObj)
                        }
                    } else {
                        state.application.documents = [...state.application.documents, docObj]
                    }
                }
            }
        },
        addMortgageAdministratorFinancialDocuments(state, action){
            state.application.documents = [...state.application.documents.filter(d => d.documentType !== CONST.DOCUMENT_TYPE_FINANCIAL_STATEMENT_LETTER),action.payload]
        },
        addAgencySupportingDocument(state, action) { 
            let docObj = {...action.payload}
            if(!docObj.id) {
                docObj.id = InternalId()
                state.application.documents = [...state.application.documents, docObj]
            } else {
                // search and replace with the id
                const docId = state.application.documents.findIndex(doc => (doc.id && (doc.id.toString() === docObj.id.toString())))
                if(docId >= 0) {
                    state.application.documents.splice(docId, 1, docObj)
                } else {
                    state.application.documents = [...state.application.documents, docObj]
                }
            }
        },
        addAgencyDocumentsToUpload(state, action) { 
            const documents = action.payload.documents;
            state.application.documents = [...state.application.documents, ...documents];            
        },
        setAgencyApplicationStatus(state, action) {
            state.application.applicationStatus = action.payload
        },
        setServerRequestStatus(state, action) {
            state.serverRequestStatus = action.payload
        },
        setLicenceApplicationPaymentStatus(state, action) { 
            state.paymentStatus = action.payload
        },
        setApplicationReferenceData(state, action) {
            state.referenceData = action.payload
        },
        setConfirmSuretyBond(state, action) {
            state.application.businessLicenceData.confirmSuretyBond = action.payload
        },
        setInternalNotes(state, action) {
            state.application.businessLicenceData.internalNotes = action.payload
        },
        setInternalNoteList(state, action) {
            state.application.businessLicenceData.internalNoteList = action.payload
        },
        setAgencyApplicationLocked(state, action) {
            state.application.locked = action.payload
        },
        setAgencyApplicationLicenceStatus(state, action) { 
            state.application.licenceStatus = action.payload
        },
        setAgencyApplicationLicenceExpiryDate(state, action) { 
            state.application.endDate = action.payload
        },
        setBLTCollectionReviewData(state, action) {
            let bltReviewData = bltCollectionAgencyReviewData(action.payload)
            state.application.businessLicenceData.reviewDataCollectionDebt = bltReviewData
        },
        mergeChangesWithApplication(state, action) { 
            state.application.businessLicenceData = {
                ...state.application.businessLicenceData,
                ...action.payload 
            }
        },
        updateBLTAgencyApplicationReviewItem(state, action) { 
            const {key, item, reviewItemIndex, isRenewalApplication} = action.payload

            if(key === "documentsDataMap"
                || key === "updatedDocumentsMap"
                || (key === "suretyBondDataMap" && isRenewalApplication)
                || key === "criminalRecordsCheckInformationMap"
                || key === "insuranceInformationMap"
                || key === "confirmationInformationMap"
                || key === "airInformationMap"
                || key === "brokerConfirmationInformationMap"
            ) {
                state.application.businessLicenceData.reviewDataCollectionDebt[key] = item
                return
            }

            let reviewData = state.application.businessLicenceData.reviewDataCollectionDebt

            /* 
             This function checks the detail which is being reviewed. For now, the check is 
             only for the string values. For everything else (like arrays etc) add the key 
             in the first if case to avoid any comparison checks.
            */
            const compareReviewData = (storeItem, updatedItem, sItemIndex) => {
                // If two items are identical then the index breaks the tie
                if(!isNaN(reviewItemIndex) && reviewItemIndex !== sItemIndex) {
                    return false;
                }
                for (const _k in storeItem) {
                    if(_k === "review")
                        continue

                    if (Object.hasOwnProperty.call(storeItem, _k)) {
                        if(Object.hasOwnProperty.call(updatedItem, _k)) {
                            const e = storeItem[_k];
                            const element = updatedItem[_k];

                            if(e !== element) {
                                return false
                            }
                        } else {
                            return false
                        }
                    }
                }
                return true
            }

            if(reviewData[key] && Array.isArray(reviewData[key])) {
                let storeItems = [...reviewData[key]]
                let storeIndex = -1
                let storeItem = storeItems.find((_sItem, index) => {
                    if(_sItem.data !== null || _sItem.data !== undefined) {
                        // Hack to avoid object comparison. 
                        // Add the section key here to avoid any comparison checks
                        if((key === "suretyBondDataMap" || 
                                key === "brokerRegulatoryInformationMap" ||
                                key === "trustAccountInformationMap") && 
                                index === reviewItemIndex) { 
                            storeIndex = index
                            return true
                        } else {
                            if(compareReviewData(_sItem, item, index)) {
                                storeIndex =index
                                return true
                            }
                        }
                    }

                    return false
                })

                if(storeItem && storeIndex >= 0) {
                    storeItems.splice(storeIndex,1,item)
                    state.application.businessLicenceData.reviewDataCollectionDebt[key] = storeItems
                }
            }
        },
        saveSegueDetails(state, action) { 
            window.sessionStorage.setItem("segue",JSON.stringify(action.payload))
        },
        saveNextUrl(sate, action) { 
            window.sessionStorage.setItem("nextUrl",action.payload)
        },
        resetNextUrl(state, action) { 
            window.sessionStorage.removeItem("nextUrl")
        },
        resetSegueDetails(state, action) { 
            window.sessionStorage.removeItem("segue")
        },
        scrollWindowDown(state, action) {
            //window.scrollTo(0, 999);
            window.scrollTo({
                top: 999,
                left : 0,
                behavior : "smooth"
            })
        },
        // MRA reducers
        // setCompletedRegInfoProgram: (state, action) => {
        //     state.application.businessLicenceData.hasCompletedRegInfoProgram = action.payload;
        // },
        // setIsPrincipalBroker: (state, action) => {
        //     state.application.businessLicenceData.isPrincipalBroker = action.payload;
        // },
        // setHasRoleWithRJSC: (state, action) => {
        //     state.application.businessLicenceData.hasRoleWithRJSC = action.payload;
        // },
        // setHasWorkExperience: (state, action) => {
        //     state.application.businessLicenceData.hasWorkExperience = action.payload;
        // },
        // setIsResidentOfCanada: (state, action) => {
        //     state.application.businessLicenceData.isResidentOfCanada = action.payload;
        // },
        // setIsOldEnough: (state, action) => {
        //     state.application.businessLicenceData.isOldEnough = action.payload;
        // },
        setPreviouslyLicencedStatus: (state, action) => {
            state.application.businessLicenceData.previouslyLicencedStatus = action.payload;
        },
        setLicenceNumber: (state, action) => {
            state.application.businessLicenceData.licenceNumber = action.payload;
        },
        setPrimaryAgencyProperty: (state, action) => {
            const { key, value } = action.payload;
            state.application.businessLicenceData[key] = value;
        },
        setMortgageProperty: (state, action) => {
            const { key, value } = action.payload;
            state.application.businessLicenceData[key] = value;
        },
        setCurrentMainAgentChangeRequestDataObject: (state, action) => {
            const { payload } = action;
            let changeRequests = state.application.businessLicenceData['changeRequests'] ?? [];
            let currentChangeRequest = null;
            const currentRequestIndex = changeRequests.findIndex(req => req.changeRequestType === CONST.CHANGE_REQUEST_TYPE_MAIN_AGENT 
                        && req.changeRequestStatus === CONST.CHANGE_REQUEST_STATUS_PENDING);
            if(currentRequestIndex >= 0) {
                currentChangeRequest = changeRequests[currentRequestIndex];
            }

            if(!currentChangeRequest) {
                currentChangeRequest = newMainAgentChangeRequestWithData(payload)
                currentChangeRequest.createdBy = state.application.userEmail;
            } else { 
                const data = currentChangeRequest['changeRequestData'];
                currentChangeRequest['changeRequestData'] = {
                    ...data,
                    ...payload
                }
            }
            
            if(currentRequestIndex >= 0) {
                changeRequests.splice(currentRequestIndex,1,currentChangeRequest)
            } else { 
                changeRequests = [...changeRequests, currentChangeRequest];
            }

            state.application.businessLicenceData['changeRequests'] = changeRequests;
        },
        setCurrentMainAgentChangeRequestReadyToSubmit: (state, action) => {
            let changeRequests = state.application.businessLicenceData['changeRequests'] ?? [];
            const currentRequestIndex = changeRequests.findIndex(req => req.changeRequestType === CONST.CHANGE_REQUEST_TYPE_MAIN_AGENT 
                        && req.changeRequestStatus === CONST.CHANGE_REQUEST_STATUS_PENDING);
            if(currentRequestIndex >= 0) {
                const currentChangeRequest = changeRequests[currentRequestIndex];
                currentChangeRequest['userApplicationReviewComplete'] = true;
                currentChangeRequest['changeRequestStatus'] = CONST.CHANGE_REQUEST_STATUS_SUBMITTED;
                currentChangeRequest['submittedDateTime'] = Date.now();
                changeRequests.splice(currentRequestIndex,1,currentChangeRequest)
            }
            state.application.businessLicenceData['changeRequests'] = changeRequests;
            state.application.businessLicenceData['reviewDataCollectionDebt'] = null;
            state.application.licencingSubType = changeRequestRelatedLicenceSubType(state.application.licencingSubType);
            state.application.applicationStatus = CONST.APPLICATION_STATUS_UNREAD;
        },
        setMainAgentRetentionApplicationForApproval: (state, action) => {
            // state.application.businessLicenceData['reviewDataCollectionDebt'] = null;
           /*
            const userAuth = state.userAuth
            state.application.businessLicenceData['userApplicationReviewComplete'] = true;
            state.application.username = userAuth.csrfToken //rpid
            state.application.userEmail = userAuth?.userData?.email ?? "" //userEmail
            state.application.submittedDate = Date.now() //submittedDate
            state.application.createdBy = userAuth?.userData?.email ?? "" //createdBy
            state.application.createdDateTime =  Date.now() //createdDateTime
            */
           
            const currentChangeRequest = {};
            const currentTime = Date.now();
            currentChangeRequest['userApplicationReviewComplete'] = true;
            currentChangeRequest['changeRequestStatus'] = CONST.CHANGE_REQUEST_STATUS_SUBMITTED;
            currentChangeRequest['submittedDateTime'] = currentTime
            state.application.businessLicenceData['changeRequests'] = [currentChangeRequest];
            state.application.submittedDate = currentTime
            
            state.application.licencingSubType = changeMainAgentLicenceSubTypeForApproval(state.application.licencingSubType);
            state.application.applicationStatus = CONST.APPLICATION_STATUS_UNREAD;
        },
        setCurrentMainAgentChangeRequestForApproval: (state, action) => {
            let changeRequests = state.application.businessLicenceData['changeRequests'] ?? [];
            const currentRequestIndex = changeRequests.findIndex(req => req.changeRequestType === CONST.CHANGE_REQUEST_TYPE_MAIN_AGENT 
                        && req.changeRequestStatus === CONST.CHANGE_REQUEST_STATUS_SUBMITTED);
            if(currentRequestIndex >= 0) {
                const currentChangeRequest = changeRequests[currentRequestIndex];
                currentChangeRequest['changeRequestStatus'] = CONST.CHANGE_REQUEST_STATUS_REVIEW_COMPLETE;
                currentChangeRequest['reviewedBy'] = action?.payload?.approverEmail;
                currentChangeRequest['reviewedDateTime'] = Date.now();
                changeRequests.splice(currentRequestIndex,1,currentChangeRequest)
            }
            state.application.businessLicenceData['changeRequests'] = changeRequests;
            state.application.licencingSubType = revertChangeRequestRelatedLicenceSubType(state.application.licencingSubType);
            state.application.applicationStatus = CONST.APPLICATION_STATUS_APPROVED_ISSUED;
        },
        setMortgageCacheProperty: (state, action) => {
            const { key, value } = action.payload;
            state.mortgageCacheProperties[key] = value;
        },
        resetMRAApplication: (state) => {
            if(state.application) {
                state.application.businessLicenceData = BusinessLicenceDataInitObject()
            } else { 
                state.application = BusinessApplicationInitObject(CONST.LICENCE_SUB_TYPE_MORTGAGE_BROKERAGE_NEW_APPLICATION)
            }
        },
        resetMLApplication: (state) => {
            if(state.application) {
                state.application.businessLicenceData = BusinessLicenceDataInitObject()
            } else { 
                state.application = BusinessApplicationInitObject(CONST.LICENCE_SUB_TYPE_MORTGAGE_LENDER_NEW_APPLICATION)
            }
        },
        setLicenceTypeDetails : (state, action) => {
            state.licenceTypeDetails = action.payload
        },
        savePaymentRequestDetails : (state, action) => {
            window.sessionStorage.setItem("paymentRequestDetails",JSON.stringify(action.payload))
        },
        resetPaymentRequestDetails : (state, action) => {
            window.sessionStorage.removeItem("paymentRequestDetails")
        },
        replaceAgencyDocument : (state, action) => {
            const docObj = {...action.payload}
            
            const docIndex = state.application.documents.findIndex(d => d.id === docObj.id)
            if(docIndex !== -1) {
                const documentAfterReplace = [...state.application.documents]
                documentAfterReplace.splice(docIndex, 1, docObj)
                state.application.documents = documentAfterReplace
            }
        },
        setAgencyApplicationForChangeRequest: (state, action) => {
            const licenceSubType = changeRequestRelatedLicenceSubType(state.application.licencingSubType)
            const appData = state.application.businessLicenceData ?? {}

            state.application = {
                ...state.application, 
                licenceStatus : CONST.LICENCE_STATUS_NEW,
                applicationStatus : CONST.APPLICATION_STATUS_PENDING_PAYMENT,
                licencingSubType : licenceSubType,
                businessLicenceData: {
                    ...appData,
                    criminalHistoryData : null,
                    reviewDataCollectionDebt : null,
                    licenceSuspendedData : null,
                    userApplicationReviewComplete : false
                }
            }
        }
    },
    extraReducers : (builder) => {
        builder
            .addCase(
                setCompanyRjscDetails,
                (state, action) => {
                    if(!state.application?.businessLicenceData) { 
                        state.application.businessLicenceData = BusinessLicenceDataInitObject()
                    }
                    
                    const {businessEntity} = action.payload

                    if(businessEntity) {
                        state.application.businessLicenceData.businessName = businessEntity.businessName
                        state.application.name = businessEntity.businessName
                        state.application.businessLicenceData.registryNumber = businessEntity.registryNumber
                    }

                    state.application.businessLicenceData.companyRjscDetails = action.payload
                }
            )
    }
})

export const {
    createNewBusinessApplicationForApplicationType,
    resetPrimaryApplicationData,
    markPrimaryApplicationDelete,
    setAgencyDataForRenewal,
    setAgencyApplication,
    setCompanyTrademark,
    setAgencyBusinessContact,
    setAgencyAddressForService,
    setAgencyAddressForFinancialRecords,
    setAgencyLicenceHistoryDetails,
    setAgencyCriminalHistoryDetails,
    setAgencyWorkHistoryDetails,
    setAgencyLicenceSuspensionDetails,
    setAgencySupportingDocuments,
    setAgencyCriminalRecordsDocuments,
    setAgencyWorkHistoryDocuments,
    setAgencyBrokerCourseCertificateDocuments,
    addAgencySupportingDocument,
    addMortgageBrokerageInsuranceDocuments,
    addMortgageBrokerageAIRDocuments,
    addMortgageBrokerageInsuranceContinuationDocuments,
    addMortgageAdministratorFinancialDocuments,
    setAgencyApplicationLicenceSubType,
    addAgencyDocumentsToUpload,
    setAgencyApplicationStatus,
    setServerRequestStatus,
    setLicenceApplicationPaymentStatus,
    setApplicationReferenceData,
    setConfirmSuretyBond,
    setInternalNotes,
    setInternalNoteList,
    setAgencyApplicationLocked,
    setAgencyApplicationLicenceStatus,
    setAgencyApplicationLicenceExpiryDate,
    mergeChangesWithApplication,
    setBLTCollectionReviewData,
    updateBLTAgencyApplicationReviewItem,
    saveNextUrl,
    resetNextUrl,
    saveSegueDetails,
    resetSegueDetails,
    scrollWindowDown,
    resetMRAApplication,
    resetMLApplication,
    // setCompletedRegInfoProgram,
    // setIsPrincipalBroker,
    // setHasRoleWithRJSC,
    // setHasWorkExperience,
    // setIsResidentOfCanada,
    // setIsOldEnough,
    setPreviouslyLicencedStatus,
    setLicenceNumber,
    setMortgageProperty,
    setCurrentMainAgentChangeRequestDataObject,
    setCurrentMainAgentChangeRequestReadyToSubmit,
    setMainAgentRetentionApplicationForApproval,
    setCurrentMainAgentChangeRequestForApproval,
    setMortgageCacheProperty,
    setPrimaryAgencyProperty,
    setLicenceTypeDetails,
    savePaymentRequestDetails,
    resetPaymentRequestDetails,
    replaceAgencyDocument,
    setAgencyApplicationForChangeRequest,
    setAgencyDocumentsIsUpdatedReset
} = primaryAgencyApplicationSlice.actions

export const fetchApplicationReferenceData = () => async (dispatch, getState) => {
    return fetch(CONST.APPLICATION_REFERENCE_DATA_URL)
            .then(resp => resp.json())
            .then((data) => {
                return dispatch(setApplicationReferenceData(data))
            })
}

export const fetchLicenceTypeDetails = (licenceSubType) => async (dispatch, getState) => {
    const url = CONST.LICENCE_TYPE_DETAILS_URL + "?licenceSubType=" + licenceSubType
    return fetch(url)
            .then(resp => resp.json())
            .then((data) => {
                return dispatch(setLicenceTypeDetails(data))
            })
}

export default primaryAgencyApplicationSlice.reducer
